import React, {Component} from 'react';
import PropTypes from 'prop-types';
import styled from "styled-components";

import Select from '@mui/material/Select';
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "../utils/Autocomplete";

import {IconButton} from '@mui/material';
import {Cancel} from '@mui/icons-material';

const QUALIFIER_INCLUDED = "included"
const QUALIFIER_EXCLUDED = "excluded"

const InlineWrapper = styled.div`
  display: inline-block;
  margin-left: 1rem;
  vertical-align: middle;
`;

const AndClause = styled.div`
  display: inline-block;
`;

const OrClause = styled.div`
  display: block;
  margin-left: 3rem;
  width: 100%;
`;

const AutoCompleteWrapper = styled(InlineWrapper)`
  width: 600px;
  border: 1px solid #bbbbbb;
  padding: 0 1rem 0.75rem;
  line-height: 2.5rem;
`;

const GeneralButton = styled.div`
  width: 5rem;
  border: 2px solid #1976d2;
  text-align: center;
  color: #1976d2;
  text-transform: uppercase;
  font-weight: bold;
  margin: 0.75rem 1rem;
  line-height: 2rem;
  cursor: pointer;
`;

const AndButton = styled(GeneralButton)`
  display: inline-block;
`;

const OrButton = styled(GeneralButton)`
  display: block;
  margin-left: 2rem;
`;

const DisabledAnd = styled(AndButton)`
  cursor: default;
  border: 0;
  color: #888888;
`;

const DisabledOr = styled(OrButton)`
  cursor: default;
  border: 0;
  color: #888888;
`;

const DeleteButton = styled.div`
  display: inline-block;
  cursor: pointer;
`;

const DeleteButtonStyle = {
  color: '#cccccc',
  '&: hover': {
    color: 'maroon',
    opacity: 0.65
  }
};

class GenericTargeting extends Component {

  static propTypes = {
    tpts: PropTypes.array.isRequired,
    placement: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired
  };

  state = {
    genericTypes: {},
    value: []
  }

  componentDidMount() {
    this.loadGenericTypes()
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const {value} = this.state
    const {placement} = this.props
    const genericTargetingChanged = placement.customTargetingArr !== prevProps.placement.customTargetingArr
    if (genericTargetingChanged) {
      this.loadGenericTypes()
    } else {
      if (!value.length) {
        this.addOrCondition()
      }
    }
  }

  loadGenericTypes = () => {
    const { tpts, placement } = this.props
    let genericTypes = {}
    for (let i = 0; i < tpts.length; i++) {
      if (!genericTypes[tpts[i].type]) {
        genericTypes[tpts[i].type] = {}
      }
      genericTypes[tpts[i].type][tpts[i].name] = tpts[i].id
    }
    // Keep kvid available while its value cache is being refreshed.
    genericTypes.kvid = genericTypes.kvid || {};
    this.setState({
      genericTypes: genericTypes,
      value: placement.customTargetingArr || []
    });
  }

  changeEvent = (value) => {
    const {onChange} = this.props
    const event = {
      target: {
        name: "genericTargeting",
        value: value || []
      }
    }
    onChange(event)
  }

  buildClause = (type = "ab", qualifier = QUALIFIER_INCLUDED, items = []) => {
    return {
      type: type,
      qualifier: qualifier,
      items: items
    }
  }

  handleChange = (evt, orIndex, andIndex) => {
    const {value} = this.state
    let newValue = JSON.parse(JSON.stringify(value))
    newValue[orIndex][andIndex].items = evt
    this.changeEvent(newValue)
  };

  onChangeType = (evt, orIndex, andIndex) => {
    const {value} = this.state
    let newValue = JSON.parse(JSON.stringify(value))
    newValue[orIndex][andIndex].type = evt.target.value
    newValue[orIndex][andIndex].items = []
    this.changeEvent(newValue)
  };

  onChangeQualifier = (evt, orIndex, andIndex) => {
    const {value} = this.state
    let newValue = JSON.parse(JSON.stringify(value))
    newValue[orIndex][andIndex].qualifier = evt.target.value
    this.changeEvent(newValue)
  };

  addOrCondition = () => {
    const {value} = this.state
    let newValue = JSON.parse(JSON.stringify(value))
    const clause = this.buildClause()
    newValue.push([clause])
    this.changeEvent(newValue)
  }

  addAndCondition = (orIndex) => {
    const {value} = this.state
    let newValue = JSON.parse(JSON.stringify(value))
    const clause = this.buildClause()
    newValue[orIndex].push(clause)
    this.changeEvent(newValue)
  }

  onDelete = (orIndex, andIndex) => {
    const {value} = this.state
    let newValue = JSON.parse(JSON.stringify(value))
    newValue[orIndex].splice(andIndex, 1)
    if (!newValue[orIndex].length) {
      newValue.splice(orIndex, 1)
    }
    this.changeEvent(newValue)
  }

  render() {
    const { genericTypes } = this.state;
    const { value } = this.state
    // Keep legacy targeting types visible when an existing placement contains
    // a key that is no longer available for new targeting (for example `fr`).
    // The legacy option is rendered disabled, so it can be preserved and
    // displayed without being selectable for new or changed clauses.
    const existingTypeNames = value
      .flat()
      .map(andItem => andItem.type)
      .filter(Boolean);
    const typeNames = [...new Set([...Object.keys(genericTypes), ...existingTypeNames])].sort();
    if (!typeNames.length || !value) {
      return ''
    }
    const qualifiers = [
      {label: 'is any of', value: QUALIFIER_INCLUDED},
      {label: 'is none of', value: QUALIFIER_EXCLUDED},
    ]
    const orButton = <OrButton
      onClick={e => this.addOrCondition()}>
      {"or"}
    </OrButton>
    const orLists = value.map((andList, orIndex) => {
      const disabledOr = <DisabledOr>{"or"}</DisabledOr>
      const ands = andList.map((andItem, andIndex) => {
        const suggestions = Object.keys(genericTypes[andItem.type] || {}).map(name => {
          return {
            name: name,
            value: (genericTypes[andItem.type] || {})[name]
          }
        })
        const andButton = andIndex < andList.length - 1 ? <DisabledAnd>
            {"and"}
          </DisabledAnd> :
          <AndButton
            onClick={e => this.addAndCondition(orIndex)}>
            {"and"}
          </AndButton>
        const deleteButton = <DeleteButton>
          <IconButton
            onClick={e=>this.onDelete(orIndex, andIndex)}
            sx={DeleteButtonStyle}
            size="large">
            <Cancel/>
          </IconButton>
        </DeleteButton>
        const extraStyles = {
          fontSize: "1.125rem",
          color: '#444444',
          minWidth: '8rem',
          textAlign: 'center'
        };
        return <AndClause
          key={`AndClause_${andIndex}_${orIndex}`}>
          <InlineWrapper>
            <Select
              value={andItem.type}
              sx={extraStyles}
              variant={"standard"}
              onChange={e => this.onChangeType(e, orIndex, andIndex)}>
              {typeNames.map(name => {
                const isLegacyType = !Object.prototype.hasOwnProperty.call(genericTypes, name);
                return <MenuItem key={name} value={name} disabled={isLegacyType}>{name}</MenuItem>
              })}
            </Select>
          </InlineWrapper>
          <InlineWrapper>
            <Select
              value={andItem.qualifier}
              sx={extraStyles}
              variant={"standard"}
              onChange={e => this.onChangeQualifier(e, orIndex, andIndex)}>
              {qualifiers.map(q => {
                return <MenuItem key={q.label} value={q.value}>{q.label}</MenuItem>
              })}
            </Select>
          </InlineWrapper>
          <AutoCompleteWrapper>
            <Autocomplete
              disabled={false}
              onChange={e => this.handleChange(e, orIndex, andIndex)}
              value={andItem.items}
              suggestions={suggestions}
              name={`${andItem.type}Targeting`}
              suggestionKey="name"
              placeholder={andItem.type === 'artid' ? "Start typing - large list, will take a few seconds" : "Select or start typing"}
              label={''}
            />
          </AutoCompleteWrapper>
          {andButton}
          {deleteButton}
        </AndClause>
      })
      return [ands, orIndex < value.length - 1 ? disabledOr : '']
    })
    return <OrClause>
      {orLists}{orButton}
    </OrClause>
  }

}

export default GenericTargeting;
